import { createContext, useContext } from 'react';

/**
 * Toast access for components that are too deep to receive pushToast as a prop.
 *
 * The toast state itself stays in App.js (it owns `toasts` / `toastHistory` and
 * renders ToastBar). App simply also publishes its pushToast/dismissToast here,
 * so this is purely additive: every component that already receives pushToast
 * as a prop keeps working exactly as before, and anything else can call
 * useToast() instead of reaching for window.alert().
 *
 * Signature matches App's own: pushToast(id, type, message)
 *   id      stable per call site — pushing the same id replaces that toast
 *           rather than stacking duplicates
 *   type    'running' | 'success' | 'error' | 'info'
 *   message the text shown to the user
 *
 * The no-op default means a component rendered outside the provider (a test,
 * or a subtree mounted on its own) degrades to silence rather than crashing.
 */
const ToastContext = createContext({
  pushToast: () => {},
  dismissToast: () => {},
});

export const ToastProvider = ToastContext.Provider;

export function useToast() {
  return useContext(ToastContext);
}

export default ToastContext;
